import Konva from "konva";
import {useEncounterViewPort} from "@/encounter/viewport/EncounterViewPortHooks.ts";
import type Position from "@/util/Position.ts";

type WorldPointerHandler = (pointer: Position, event: MouseEvent) => void;

type WorldPointerSession = {
    onChange: WorldPointerHandler;
    onEnd?: WorldPointerHandler;
    requireMovement?: boolean;
    onCancel?: () => void;
}

const onWindowMouseMove = (
    onMouseMove: (event: MouseEvent) => void,
    onMouseUp?: (event: MouseEvent) => void,
) => {
    const up = (event: MouseEvent) => {
        onMouseUp?.(event);
        window.removeEventListener("mousemove", onMouseMove);
        window.removeEventListener("mouseup", up);
    };
    window.addEventListener("mousemove", onMouseMove);
    window.addEventListener("mouseup", up);
}

/**
 * Provides the coordinate conversion and pointer tracking shared by draggable
 * tabletop elements. Consumers only receive world coordinates and therefore do
 * not need to know about the current viewport translation or scale.
 */
export function useTableTopInteraction() {
    const {mapTransform} = useEncounterViewPort();

    const toWorldPosition = (position: Position): Position => ({
        x: (position.x - mapTransform.x) / mapTransform.scale,
        y: (position.y - mapTransform.y) / mapTransform.scale,
    });

    const getWorldPointer = (stage: Konva.Stage): Position | undefined => {
        const pointer = stage.getPointerPosition();
        return pointer ? toWorldPosition(pointer) : undefined;
    };

    /**
     * Tracks the pointer on the window so an interaction continues when the
     * cursor leaves the canvas. Konva's stage pointer is synchronized first.
     */
    const trackWorldPointer = (stage: Konva.Stage, session: WorldPointerSession) => {
        const startPointer = getWorldPointer(stage);
        if (!startPointer) return false;
        let lastPointer = startPointer;
        let moved = false;

        const getPointerFromEvent = (event: MouseEvent) => {
            stage.setPointersPositions(event);
            const pointer = getWorldPointer(stage);
            if (pointer) {
                if (pointer.x !== lastPointer.x || pointer.y !== lastPointer.y) moved = true;
                lastPointer = pointer;
            }
            return pointer;
        };
        const handlePointerChange = (event: MouseEvent) => {
            const pointer = getPointerFromEvent(event);
            if (pointer && (!session.requireMovement || moved)) session.onChange(pointer, event);
        };
        const handlePointerEnd = (event: MouseEvent) => {
            const pointer = getPointerFromEvent(event) ?? lastPointer;
            if (session.requireMovement && !moved) {
                session.onCancel?.();
                return;
            }
            (session.onEnd ?? session.onChange)(pointer, event);
        };

        onWindowMouseMove(handlePointerChange, handlePointerEnd);
        return true;
    };

    /** Reports translation only after movement; a selection click cancels the gesture. */
    const startWorldTranslation = (
        stage: Konva.Stage,
        session: {
            onChange: (delta: Position) => void;
            onEnd?: (delta: Position) => void;
            onCancel?: () => void;
        },
    ) => {
        const startPointer = getWorldPointer(stage);
        if (!startPointer) return false;

        const getTranslation = (currentPointer: Position) => ({
            x: currentPointer.x - startPointer.x,
            y: currentPointer.y - startPointer.y,
        });
        return trackWorldPointer(stage, {
            requireMovement: true,
            onCancel: session.onCancel,
            onChange: currentPointer => {
                session.onChange(getTranslation(currentPointer));
            },
            onEnd: currentPointer => session.onEnd?.(getTranslation(currentPointer)),
        });
    };

    return {toWorldPosition, getWorldPointer, trackWorldPointer, startWorldTranslation};
}
